import React from 'react';
import { Link } from 'react-router-dom';
import './Services.css';
import QuoteForm from '../../components/QuoteForm';
import SEO from '../../components/SEO';

const Services = () => {
  const services = [
    {
      id: 'gypsum-ceiling',
      title: 'Gypsum Ceiling Solutions',
      description: 'Modern, elegant ceiling designs with premium gypsum materials. Transform your space with our custom ceiling solutions.',
      icon: '🏗️',
      route: '/gypsum-ceiling',
      features: ['Custom ceiling designs', 'Modern finishes', 'Durable materials', 'Professional installation']
    },
    {
      id: 'wainscoting',
      title: 'Wainscoting Panels',
      description: 'Decorative wall paneling that adds elegance and protection to your walls. Classic and modern designs available.',
      icon: '🎨',
      route: '/wain-scouting',
      features: ['Decorative wall panels', 'Custom designs', 'Premium materials', 'Enhanced aesthetics']
    },
    {
      id: 'tv-gypsum-wall',
      title: 'TV & Gypsum Wall Units',
      description: 'Custom entertainment centers and feature walls that combine functionality with stunning design.',
      icon: '📺',
      route: '/tv-gypsum-wall',
      features: ['Custom TV units', 'Entertainment centers', 'Feature walls', 'Storage solutions']
    },
    {
      id: 'kitchen-wardrobe',
      title: 'Kitchen & Wardrobe Fitting',
      description: 'Professional fitting services for kitchens and wardrobes, maximizing space and enhancing functionality.',
      icon: '🍳',
      route: '/kitchen-wardrobe-fitting',
      features: ['Custom kitchen fitting', 'Wardrobe installation', 'Space optimization', 'Quality hardware']
    }
  ];

  const whyChooseUs = [
    {
      title: 'Professional Expertise',
      description: 'Over 10 years of experience in interior design and construction services'
    },
    {
      title: 'Quality Materials',
      description: 'We use only premium, certified materials for all our projects'
    },
    {
      title: 'Affordable Pricing',
      description: 'Competitive rates without compromising on quality or safety'
    },
    {
      title: 'Timely Delivery',
      description: 'We respect your time and complete projects on schedule'
    },
    {
      title: 'Customer Satisfaction',
      description: '100% satisfaction guarantee with dedicated support throughout'
    },
    {
      title: 'Licensed & Insured',
      description: 'Fully certified professionals with comprehensive insurance coverage'
    }
  ];

  const process = [
    {
      step: '1',
      title: 'Consultation',
      description: 'Free assessment of your needs and project requirements'
    },
    {
      step: '2',
      title: 'Design & Planning',
      description: 'Custom solutions tailored to your space and budget'
    },
    {
      step: '3',
      title: 'Quotation',
      description: 'Transparent pricing with detailed breakdown of costs'
    },
    {
      step: '4',
      title: 'Execution',
      description: 'Professional installation by our expert team'
    },
    {
      step: '5',
      title: 'Quality Check',
      description: 'Thorough inspection to ensure highest standards'
    },
    {
      step: '6',
      title: 'After-Service Support',
      description: 'Ongoing maintenance and warranty support'
    }
  ];

  const pricingGuide = [
    {
      title: 'Full Installation (Materials + Labour)',
      items: [
        'Gypsum ceiling: from Ksh 2,500 - 3,500 per sqm',
        'Kitchen cabinets: from Ksh 450,000 (varies with size and layout)',
        'Wardrobes: from Ksh 100,000 (depending on size and design)'
      ]
    },
    {
      title: 'Labour Only (Client Supplies Materials)',
      items: [
        'Gypsum ceiling labour: Ksh 1,000 per sqm',
        'Kitchen & wardrobe installation: 30% of total material cost'
      ]
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Interior Design Services",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Techie Interiors",
      "image": "https://techieinteriors.co.ke/techLogo.jpeg",
      "telephone": "+254748600208 ",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Nairobi",
        "addressCountry": "KE"
      }
    },
    "areaServed": {
      "@type": "Country",
      "name": "Kenya"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Interior Design Services",
      "itemListElement": services.map(service => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": service.title
        }
      }))
    }
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Why choose Techie Interiors?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Techie Interiors offers affordable, customized interior design services in Nairobi Kenya with expert installation. Over 10 years of experience, quality materials, and 100% satisfaction guarantee."
        }
      },
      {
        "@type": "Question",
        "name": "What interior design services do you offer in Kenya?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We offer gypsum ceiling installation, electrical services, soundproofing, kitchen & wardrobe fitting, TV & gypsum wall units, wainscoting panels, 3D rendering & visualization, and scaffolding hire."
        }
      },
      {
        "@type": "Question",
        "name": "Do you offer free consultations?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, we provide free assessment of your needs and project requirements. Contact us today to schedule your consultation."
        }
      }
    ]
  };

  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://techieinteriors.co.ke/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Services",
        "item": "https://techieinteriors.co.ke/services"
      }
    ]
  };

  return (
    <div className="services-page">
      {/* SEO Component */}
      <SEO
        title="Interior Design Services in Nairobi & Kenya | Techie Interiors"
        description="Explore Techie Interiors services for residential and commercial interiors in Nairobi and Kenya, including gypsum ceilings, TV wall units, wall panels, kitchen cabinets, wardrobes, and office fit-outs."
        image="/techLogo.jpeg"
        url="https://techieinteriors.co.ke/services"
        structuredData={[structuredData, faqStructuredData, breadcrumbStructuredData]}
      />
      {/* Hero Section */}
      <section className="services-hero">
        <div className="hero-overlay">
          <div className="hero-content">
            <h1>Comprehensive Interior Design & Construction Services in Kenya</h1>
            <p>From concept to completion, Techie Interiors delivers professional gypsum work, electrical services, soundproofing, custom fittings, and 3D visualization for residential and commercial projects across Kenya.</p>
            <a href="#contact" className="cta-button">Get a Free Consultation</a>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="services-intro">
        <div className="container">
          <h2>Professional Interior Solutions Tailored to Your Needs</h2>
          <p className="intro-text">
            At <strong>Techie Interiors</strong>, we specialize in transforming spaces with expert craftsmanship and modern design solutions. Whether you're renovating your home, building a new office, or upgrading your commercial space, our comprehensive range of services ensures every aspect of your project is handled by skilled professionals.
          </p>
          <p className="intro-text">
            From stunning <Link to="/gypsum-ceiling"><strong>gypsum ceiling installations</strong></Link> and <Link to="/wain-scouting"><strong>wall panel work</strong></Link> to advanced <Link to="/tv-gypsum-wall"><strong>TV wall units</strong></Link> and custom <Link to="/kitchen-wardrobe-fitting"><strong>kitchen and wardrobe fittings</strong></Link>, we deliver quality workmanship that exceeds expectations. Our team combines technical expertise with creative vision to create spaces that are both beautiful and functional. <Link to="/contact-us"><strong>Get in touch</strong></Link> to learn more about our services and how we can help you transform your space.
          </p>
        </div>
      </section>

      {/* All Services Section */}
      <section className="all-services">
        <div className="container">
          <h2>Our Complete Range of Services</h2>
          <p className="section-subtitle">Click on any service to learn more about our comprehensive solutions</p>
          
          <div className="services-grid">
            {services.map((service) => (
              <article key={service.id} className="service-card">
                <div className="service-icon">{service.icon}</div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <ul className="service-features">
                  {service.features.map((feature, index) => (
                    <li key={index}>✓ {feature}</li>
                  ))}
                </ul>
                <Link
                  to={service.route}
                  className="learn-more"
                  aria-label={`Learn more about ${service.title}`}
                >
                  Learn More →
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-services">
        <div className="container">
          <h2>Why Choose Techie Interiors?</h2>
          <div className="why-choose-grid">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="why-choose-card">
                <div className="why-icon">{item.icon}</div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Process Section */}
      <section className="services-process">
        <div className="container">
          <h2>How We Work</h2>
          <div className="process-steps">
            {process.map((step, index) => (
              <div key={index} className="process-step">
                <div className="step-number">{step.step}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Residential & Commercial Section */}
      <section className="residential-commercial">
        <div className="container">
          <h2>Services for Every Need</h2>
          <div className="rc-grid">
            <div className="rc-card">
              <h3>🏠 Residential Services</h3>
              <p>Transform your home with our expert interior solutions:</p>
              <ul>
                <li>Custom gypsum ceiling designs</li>
                {/* <li>Electrical wiring & lighting installation</li>
                <li>Soundproofing for peace and privacy</li> */}
                <li>Kitchen & wardrobe fitting</li>
                <li>TV units & entertainment centers</li>
                <li>Decorative wall paneling</li>
              </ul>
            </div>
            <div className="rc-card">
              <h3>🏢 Commercial Services</h3>
              <p>Professional solutions for business spaces:</p>
              <ul>
                {/* <li>Office electrical systems</li> */}
                {/* <li>Commercial soundproofing</li> */}
                <li>Retail space design & installation</li>
                <li>Conference room solutions</li>
                <li>3D visualization for planning</li>
                <li>Scaffolding for large projects</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Guide Section */}
      <section className="pricing-guide">
        <div className="container">
          <h2>Guide Pricing</h2>
          <p className="pricing-intro">
            Prices vary depending on design, size, and materials used.
          </p>
          <div className="pricing-grid">
            {pricingGuide.map((section, index) => (
              <div key={index} className="pricing-card">
                <h3>{section.title}</h3>
                <ul>
                  {section.items.map((item, itemIndex) => (
                    <li key={itemIndex}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="pricing-note">
            <p>
              <strong>Note:</strong> Final quotation is provided after site visit and design discussion.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="services-contact" id="contact">
        <div className="container">
          <h2>Request a Service Quote</h2>
          <div className="contact-wrapper">
            <div className="contact-info">
              <h3>Get In Touch</h3>
              <p>Ready to start your project? Contact us today for professional consultation and competitive pricing.</p>
              <div className="info-item">
                <strong>Phone:</strong>
                <span>+254748600208</span>
              </div>
              <div className="info-item">
                <strong>Email:</strong>
                <span>info@techieinteriors.co.ke</span>
              </div>
              <div className="info-item">
                <strong> Location:</strong>
                <span>Nairobi, Kenya</span>
              </div>
              <div className="info-item">
                <strong>Working Hours:</strong>
                <span>Mon - Sat: 8:00 AM - 6:00 PM</span>
              </div>
              <p className="trust-line"><strong>We respond instantly.</strong></p>
            </div>
            <div className="contact-form">
              <QuoteForm serviceName="General Service Inquiry" />
            </div>
          </div>
        </div>
      </section>

      {/* SEO Footer Section */}
      <section className="services-seo-footer">
        <div className="container">
          <h2>Interior Design Services in Kenya | Gypsum, Electrical, Tv & Gypsum wall units, Kitchen & Wardrobe fitting & More</h2>
          <p>
            <Link to="/" className="services-seo-footer-link"><strong>Techie Interiors</strong></Link>{' '}
            is a trusted Nairobi-based provider of modern interior services. We specialize in{' '}
            <Link to="/gypsum-ceiling" className="services-seo-footer-link"><strong>gypsum ceiling installation</strong></Link>,{' '}
            <Link to="/tv-gypsum-wall" className="services-seo-footer-link"><strong>TV wall units</strong></Link>,{' '}
            <Link to="/kitchen-cabinets-nairobi" className="services-seo-footer-link"><strong>kitchen cabinets</strong></Link>,{' '}
            <Link to="/kitchen-wardrobe-fitting" className="services-seo-footer-link"><strong>custom wardrobes</strong></Link>,{' '}
            <Link to="/wain-scouting" className="services-seo-footer-link"><strong>wall panels</strong></Link>, and complete{' '}
            <Link to="/project" className="services-seo-footer-link"><strong>home and office fit-outs</strong></Link>.
          </p>
          <p>
            Our professional team serves residential and commercial clients across{' '}
            <Link to="/interior-design-nairobi" className="services-seo-footer-link"><strong>Nairobi</strong></Link>{' '}
            and major towns in Kenya. Whether you need a home transformation, workspace renovation, or a commercial interior solution, we deliver quality workmanship and premium finishes.
          </p>
          <p>
            <Link to="/contact-us" className="services-seo-footer-link"><strong>Contact Techie Interiors</strong></Link>{' '}
            today for expert{' '}
            <Link to="/interior-design-nairobi" className="services-seo-footer-link"><strong>interior design services</strong></Link>,{' '}
            <Link to="/gypsum-ceiling" className="services-seo-footer-link"><strong>ceiling installation</strong></Link>,{' '}
            <Link to="/tv-gypsum-wall" className="services-seo-footer-link"><strong>Tv &amp; Gypsum wall unit</strong></Link>,{' '}
            <Link to="/kitchen-wardrobe-fitting" className="services-seo-footer-link"><strong>Kitchen &amp; Wardrobe fittings</strong></Link>, and custom{' '}
            <Link to="/wain-scouting" className="services-seo-footer-link"><strong>Wainscoting panels</strong></Link> that transform your space into something extraordinary.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Services;
